// ===========================================
// AlphaVar Explorer - Type Definitions
// ===========================================

// ===========================================
// Clinical Context Types (Cardiology VUS Assessment)
// ===========================================

export type CardiacPhenotype =
  | 'hcm'
  | 'dcm'
  | 'arvc'
  | 'lvnc'
  | 'rcm'
  | 'lqts'
  | 'sqts'
  | 'brugada'
  | 'cpvt'
  | 'wpw'
  | 'afib_early'
  | 'scd_survivor'
  | 'aortopathy'
  | 'none'
  | 'other';

export const CARDIAC_PHENOTYPE_LABELS: Record<CardiacPhenotype, string> = {
  hcm: 'HCM',
  dcm: 'DCM',
  arvc: 'ARVC',
  lvnc: 'LVNC',
  rcm: 'RCM',
  lqts: 'Long QT Syndrome',
  sqts: 'Short QT Syndrome',
  brugada: 'Brugada Syndrome',
  cpvt: 'CPVT',
  wpw: 'WPW',
  afib_early: 'Early-Onset AF',
  scd_survivor: 'SCD Survivor',
  aortopathy: 'Aortopathy',
  none: 'No Cardiac Phenotype',
  other: 'Other',
};

export interface ClinicalContext {
  phenotype: CardiacPhenotype | null;
  age: number | null;
  sex: 'male' | 'female' | null;
  familyHistorySCD: 'yes' | 'no' | 'unknown' | null;
  echoFindings: string | null;
  ecgFindings: string | null;
  symptoms: string | null;
  priorGeneticTesting: string | null;
}

export const EMPTY_CLINICAL_CONTEXT: ClinicalContext = {
  phenotype: null,
  age: null,
  sex: null,
  familyHistorySCD: null,
  echoFindings: null,
  ecgFindings: null,
  symptoms: null,
  priorGeneticTesting: null,
};

export interface PhenotypeGenotypeFit {
  concordance: 'high' | 'moderate' | 'low' | 'insufficient_data';
  summary: string;
  keyFactors: string[];
  penetranceNote: string | null;
}

export interface ClinicalActionPlan {
  imaging: string | null;
  rhythmMonitoring: string | null;
  exerciseGuidance: string | null;
  familyScreening: string | null;
  geneticCounseling: string | null;
  reclassificationTimeline: string | null;
  additionalWorkup: string | null;
}

export interface NearbyPathogenicVariant {
  proteinChange: string;
  classification: string;
  condition: string | null;
  reviewStatus: string | null;
  distance: number;
  clinvarId: string | null; // ClinVar variation ID for linking
  clinvarUrl: string | null; // Direct URL to ClinVar entry
}

export interface ReclassificationInfo {
  currentClassification: string | null;
  submitterCount: number;
  reviewStars: number;
  lastEvaluated: string | null;
  hasBeenReclassified: boolean;
  previousClassification: string | null;
  reclassificationDate: string | null;
}

export type ClinicalFindingSource = 'ClinVar' | 'PubMed' | 'Nearby ClinVar';
export type ClinicalFindingEvidenceType = 'classification' | 'case_report' | 'cohort' | 'functional' | 'review' | 'unknown';
export type ClinicalFindingMatchConfidence = 'exact_variant' | 'nearby_variant' | 'probable_variant' | 'variant_database_link' | 'gene_level';

export interface ReportedClinicalFinding {
  source: ClinicalFindingSource;
  evidenceType: ClinicalFindingEvidenceType;
  condition: string | null;
  reportedPhenotype: string | null;
  keyFinding: string;
  accession: string | null;
  pmid: number | null;
  citation: string | null;
  year: string | null;
  url: string | null;
  matchConfidence: ClinicalFindingMatchConfidence;
  inheritancePattern: string | null;
  variantProximity: string | null;
}

export interface ReportedClinicalFindings {
  summary: string;
  findings: ReportedClinicalFinding[];
  exactVariantEvidenceCount: number;
  nearbyVariantEvidenceCount: number;
  geneLevelEvidenceCount: number;
}

export type InheritancePattern = 'AD' | 'AR' | 'X-linked' | 'mitochondrial' | 'multifactorial' | 'other' | 'unknown';
export type GeneDiseaseEvidenceLevel = 'Definitive' | 'Strong' | 'Moderate' | 'Limited' | 'Disputed' | 'Refuted' | 'Unknown';

export interface GeneDiseasePhenotypeCorrelation {
  phenotype: string;
  disease: string;
  inheritance: InheritancePattern[];
  evidenceLevel: GeneDiseaseEvidenceLevel;
  summary: string;
}

export interface GeneDiseaseCorrelation {
  diseases: string[];
  inheritance: InheritancePattern[];
  summary: string;
  evidenceLevel: GeneDiseaseEvidenceLevel | null;
  phenotypeCorrelations: GeneDiseasePhenotypeCorrelation[];
}

// ===========================================
// Multi-Scale Analysis Types (NEW)
// ===========================================

export interface RegionMetrics {
  window_name: string;
  window_size_bp: number;
  reference_mean: number | null;
  alternate_mean: number | null;
  reference_max: number | null;
  alternate_max: number | null;
  expression_difference: number | null;
  expression_change_percent: number | null;
  expression_impact: string | null;
  expression_direction: string | null;
}

export interface TrackMetrics {
  track_index: number;
  track_name: string | null;
  reference_mean: number | null;
  alternate_mean: number | null;
  expression_change_percent: number | null;
  expression_impact: string | null;
}

// ===========================================
// AlphaGenome Types (Enhanced)
// ===========================================

export interface AlphaGenomeData {
  source: string;
  has_reference_rna_seq: boolean;
  has_alternate_rna_seq: boolean;
  interval?: {
    chromosome: string;
    start: number;
    end: number;
  };
  requested_outputs?: string[];
  ontology_terms?: string[];
  tissue_type?: string;
  
  // Expression analysis
  expression_difference?: number | null;
  expression_change_percent?: number | null;
  reference_expression_mean?: number | null;
  alternate_expression_mean?: number | null;
  reference_expression_max?: number | null;
  alternate_expression_max?: number | null;
  expression_impact?: 'major' | 'moderate' | 'mild' | 'minimal' | string | null;
  expression_direction?: 'increased' | 'decreased' | 'unchanged' | string | null;
  clinical_interpretation?: string | null;
  
  // Multi-scale analysis (NEW)
  region_metrics?: RegionMetrics[] | null;
  track_metrics?: TrackMetrics[] | null;
  num_tracks?: number | null;
  track_names?: string[] | null;
  variant_index_in_window?: number | null;
  analysis_window_used?: string | null;
}

export interface AlphaGenomeRequest {
  chromosome: string;
  position: number;
  reference_bases: string;
  alternate_bases: string;
  window_bp?: number;
  ontology_terms?: string[];
  tissue_type?: string;
  variant_type?: string;
  gene_symbol?: string;
}

export interface AlphaGenomeResponse {
  source: string;
  has_reference_rna_seq: boolean;
  has_alternate_rna_seq: boolean;
  interval: {
    chromosome: string;
    start: number;
    end: number;
  };
  requested_outputs?: string[];
  ontology_terms?: string[];
  tissue_type?: string;
  error?: string;
  
  // Expression analysis
  expression_difference?: number | null;
  expression_change_percent?: number | null;
  reference_expression_mean?: number | null;
  alternate_expression_mean?: number | null;
  reference_expression_max?: number | null;
  alternate_expression_max?: number | null;
  expression_impact?: 'major' | 'moderate' | 'mild' | 'minimal' | string | null;
  expression_direction?: 'increased' | 'decreased' | 'unchanged' | string | null;
  clinical_interpretation?: string | null;
  
  // Variant context
  variant_type?: string;
  gene_symbol?: string;
  
  // Track metadata (NEW)
  num_tracks?: number | null;
  track_names?: string[] | null;
  
  // Multi-scale analysis (NEW)
  region_metrics?: RegionMetrics[] | null;
  track_metrics?: TrackMetrics[] | null;
  variant_index_in_window?: number | null;
  analysis_window_used?: string | null;
  
  // Debug info
  debug_info?: Record<string, unknown>;
}

// ===========================================
// Splice Prediction Scores
// ===========================================

export interface SplicePredictions {
  // SpliceAI delta scores
  spliceAI_DS_AG: number | null;  // Acceptor Gain
  spliceAI_DS_AL: number | null;  // Acceptor Loss
  spliceAI_DS_DG: number | null;  // Donor Gain
  spliceAI_DS_DL: number | null;  // Donor Loss
  spliceAI_maxDelta: number | null; // Max of all 4 deltas
  spliceAI_symbol: string | null;

  // MaxEntScan
  maxEntScan_ref: number | null;
  maxEntScan_alt: number | null;
  maxEntScan_diff: number | null;

  // dbscSNV
  dbscSNV_ada: number | null;
  dbscSNV_rf: number | null;

  // EVE
  eveScore: number | null;
  eveClass: string | null;

  // Transcript consequence terms (splice-relevant)
  spliceConsequences: string[];
}

// ===========================================
// Conservation & Constraint Scores
// ===========================================

export interface ConservationScores {
  gerp: number | null;
  phyloP: number | null;
  phastCons: number | null;
}

export interface LofScores {
  pLI: number | null;
  loeuf: number | null;
  lofHcCount: number | null;
}

export interface CaddScore {
  raw: number | null;
  phred: number | null;
}

export interface NmdPrediction {
  isLastExon: boolean;
  distanceToLastExonJunction: number | null;
  predictedNmdEscape: boolean;
  explanation: string;
}

// ===========================================
// Main Variant Data
// ===========================================

export interface VariantData {
  gene: string;
  proteinChange: string;
  variantType: string;
  genomicCoordinates: string;
  hgvsGenomic: string;
  alphaMissenseScore: number;
  alphaMissenseClass: string;
  scoreSource: string;
  clinicalSignificance: string | null;
  molecularMechanism: string;
  structuralImpact: string;
  description: string;
  geneDiseaseCorrelation: GeneDiseaseCorrelation | null;
  
  // AlphaGenome data (enhanced)
  alphaGenomePrediction: string;
  alphaGenomeData: AlphaGenomeData | null;
  alphaGenomeExpressionImpact: string | null;
  alphaGenomeExpressionChange: number | null;
  alphaGenomeTissue: string | null;
  
  // Multi-scale AlphaGenome analysis (NEW)
  alphaGenomeRegionMetrics: RegionMetrics[] | null;
  alphaGenomeTrackMetrics: TrackMetrics[] | null;
  alphaGenomeMostAffectedRegion: RegionMetrics | null;
  alphaGenomeMostAffectedTrack: TrackMetrics | null;
  
  // Extended data
  alleleString: string;
  siftScore: number | null;
  siftPrediction: string | null;
  polyphenScore: number | null;
  polyphenPrediction: string | null;
  canonicalTranscript: string | null;
  proteinId: string | null;
  aminoAcids: string | null;
  codons: string | null;
  clinvarData: ClinVarSummary | null;
  populationFrequencies: PopulationFrequencies | null;
  cosmicIds: string[];
  hgmdIds: string[];
  pubmedIds: number[];
  transcriptCount: number;
  
  // Loss-of-Function specific
  isLofVariant: boolean;
  lofMechanism: string | null;
  nmdPrediction: NmdPrediction | null;
  lofScores: LofScores | null;
  conservationScores: ConservationScores | null;
  caddScore: CaddScore | null;
  revelScore?: number | null;
  predictivePathogenicityScore: number | null;
  predictivePathogenicityModel: string | null;
  
  // Splice predictions (for splice variants)
  splicePredictions: SplicePredictions | null;

  // Variant type flags for UI
  isMissenseVariant: boolean;
  isNonsenseVariant: boolean;
  isFrameshiftVariant: boolean;
  isSpliceVariant: boolean;
  isSynonymousVariant: boolean;
  
  // Clinical context info
  clinicalContextLoaded: boolean;
  clinicalContextVersion: string | null;
  hasGeneSpecificGuidance: boolean;

  // Clinical context features
  clinicalContext: ClinicalContext | null;
  phenotypeGenotypeFit: PhenotypeGenotypeFit | null;
  clinicalActionPlan: ClinicalActionPlan | null;
  nearbyPathogenicVariants: NearbyPathogenicVariant[];
  reclassificationInfo: ReclassificationInfo | null;
  reportedClinicalFindings: ReportedClinicalFindings | null;
}

export interface ClinVarSummary {
  clinicalSignificance: string[];
  reviewStatus: string | null;
  conditions: string[];
  variantIds: string[];
  somaticClassification: string | null;
}

export interface PopulationFrequencies {
  globalAF: number | null;
  gnomadGenomes: number | null;
  gnomadExomes: number | null;
  gnomadPopulations: {
    afr: number | null;
    amr: number | null;
    asj: number | null;
    eas: number | null;
    fin: number | null;
    nfe: number | null;
    sas: number | null;
  };
}

export interface AnalysisState {
  status: 'idle' | 'loading' | 'success' | 'error';
  data: VariantData | null;
  error: string | null;
}

// ===========================================
// Ensembl VEP API Types
// ===========================================

export interface EnsemblVEPResponse {
  id: string;
  input: string;
  assembly_name: string;
  seq_region_name: string;
  start: number;
  end: number;
  strand: number;
  allele_string: string;
  most_severe_consequence: string;
  transcript_consequences?: TranscriptConsequence[];
  colocated_variants?: ColocatedVariant[];
}

export interface TranscriptConsequence {
  gene_symbol: string;
  gene_id: string;
  transcript_id: string;
  protein_id?: string;
  amino_acids?: string;
  codons?: string;
  protein_start?: number;
  protein_end?: number;
  consequence_terms?: string[];
  impact: string;
  hgvsc?: string;
  hgvsp?: string;
  
  // AlphaMissense
  am_class?: string;
  am_pathogenicity?: number;
  alphamissense?: {
    am_class?: string;
    am_pathogenicity?: number;
  };
  
  // SIFT and PolyPhen
  sift_score?: number;
  sift_prediction?: string;
  polyphen_score?: number;
  polyphen_prediction?: string;
  
  // Conservation
  conservation?: number;
  
  // CADD
  cadd_raw?: number;
  cadd_phred?: number;
  
  // REVEL
  revel_score?: number | string;
  
  // SpliceAI
  spliceai?: {
    SYMBOL?: string;
    DS_AG?: number;
    DS_AL?: number;
    DS_DG?: number;
    DS_DL?: number;
    DP_AG?: number;
    DP_AL?: number;
    DP_DG?: number;
    DP_DL?: number;
  };

  // MaxEntScan
  maxentscan_ref?: number | string;
  maxentscan_alt?: number | string;
  maxentscan_diff?: number | string;

  // EVE
  eve_score?: number | string;
  eve_class?: string;

  // dbscSNV (via dbNSFP) - Ensembl returns lowercase field names
  dbscsnv_ada_score?: number | string;
  dbscsnv_rf_score?: number | string;

  // Additional fields
  biotype?: string;
  strand?: number;
  cdna_start?: number;
  cdna_end?: number;
  cds_start?: number;
  cds_end?: number;
  variant_allele?: string;
  hgnc_id?: string;
  gene_symbol_source?: string;
  distance?: number;
  exon?: string;
  intron?: string;
  canonical?: number;
}

export interface ColocatedVariant {
  id: string;
  allele_string: string;
  start: number;
  end: number;
  strand: number;
  seq_region_name?: string;
  frequencies?: {
    [allele: string]: {
      af?: number;
      gnomadg?: number;
      gnomade?: number;
      gnomadg_afr?: number;
      gnomadg_amr?: number;
      gnomadg_asj?: number;
      gnomadg_eas?: number;
      gnomadg_fin?: number;
      gnomadg_nfe?: number;
      gnomadg_sas?: number;
      gnomade_afr?: number;
      gnomade_amr?: number;
      gnomade_asj?: number;
      gnomade_eas?: number;
      gnomade_fin?: number;
      gnomade_nfe?: number;
      gnomade_sas?: number;
      [key: string]: number | undefined;
    };
  };
  clin_sig?: string[];
  clin_sig_allele?: string;
  clinvar_somatic_classification?: string;
  phenotype_or_disease?: number;
  somatic?: number;
  var_synonyms?: {
    ClinVar?: string[];
    COSMIC?: string[];
    [key: string]: string[] | undefined;
  };
  pubmed?: number[];
}

// ===========================================
// Parsed Variant Types
// ===========================================

export interface ParsedVariant {
  gene: string;
  proteinChange: string | null;
  hgvsNotation: string | null;
  chromosome: string | null;
  position: number | null;
  referenceAllele: string | null;
  alternateAllele: string | null;
  variantType: VariantType;
}

export type VariantType =
  | 'missense'
  | 'nonsense'
  | 'frameshift'
  | 'indel'
  | 'splice'
  | 'synonymous'
  | 'unknown';

// ===========================================
// Chat Types
// ===========================================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
}

// ===========================================
// Utility Types & Constants
// ===========================================

export type PathogenicityClassification =
  | 'Likely Pathogenic'
  | 'Likely Benign'
  | 'Ambiguous'
  | 'Not Available';

export type ImpactLevel = 'HIGH' | 'MODERATE' | 'LOW' | 'MODIFIER' | 'UNKNOWN';

export type ExpressionImpact = 'major' | 'moderate' | 'mild' | 'minimal';

export type ExpressionDirection = 'increased' | 'decreased' | 'unchanged';

// Analysis window names
export type AnalysisWindowName = 'immediate' | 'local' | 'extended' | 'promoter';

// Score thresholds (can be used for consistent classification)
export const SCORE_THRESHOLDS = {
  ALPHAMISSENSE: {
    PATHOGENIC: 0.564,
    BENIGN: 0.34,
  },
  PLI: {
    LOF_INTOLERANT: 0.9,
    MODERATE: 0.5,
  },
  LOEUF: {
    CONSTRAINED: 0.35,
    MODERATE: 0.6,
  },
  SIFT: {
    DELETERIOUS: 0.05,
  },
  POLYPHEN: {
    PROBABLY_DAMAGING: 0.908,
    POSSIBLY_DAMAGING: 0.446,
  },
  CADD: {
    LIKELY_DELETERIOUS: 20,
    POTENTIALLY_DELETERIOUS: 15,
  },
  REVEL: {
    LIKELY_PATHOGENIC: 0.75,
    LIKELY_BENIGN: 0.5,
  },
  SPLICEAI: {
    HIGH: 0.8,
    MODERATE: 0.5,
    LOW: 0.2,
  },
  DBSCSNV: {
    SPLICE_AFFECTING: 0.6,
  },
  EVE: {
    PATHOGENIC: 0.75,
    BENIGN: 0.3,
  },
} as const;

// Analysis window sizes (in base pairs, half-window)
export const ANALYSIS_WINDOWS = {
  immediate: 100,    // ±100bp
  local: 1000,       // ±1kb
  extended: 5000,    // ±5kb
  promoter: 10000,   // ±10kb
} as const;

// ===========================================
// Helper Type Guards
// ===========================================

export function isRegionMetrics(obj: unknown): obj is RegionMetrics {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'window_name' in obj &&
    'window_size_bp' in obj
  );
}

export function isTrackMetrics(obj: unknown): obj is TrackMetrics {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'track_index' in obj
  );
}

export function hasSignificantExpressionChange(
  metrics: RegionMetrics | TrackMetrics | null,
  threshold: number = 5
): boolean {
  if (!metrics || metrics.expression_change_percent === null) {
    return false;
  }
  return Math.abs(metrics.expression_change_percent) >= threshold;
}

export function getImpactColor(impact: string | null | undefined): string {
  switch (impact?.toLowerCase()) {
    case 'major':
      return 'text-red-600';
    case 'moderate':
      return 'text-orange-500';
    case 'mild':
      return 'text-yellow-500';
    case 'minimal':
      return 'text-green-500';
    default:
      return 'text-gray-500';
  }
}

export function getDirectionIcon(direction: string | null | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'increased':
      return '↑';
    case 'decreased':
      return '↓';
    case 'unchanged':
      return '→';
    default:
      return '?';
  }
}

export function formatExpressionChange(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}
